import { useMemo, useState } from 'react';
import { ScrollView, TextInput, View, useWindowDimensions } from 'react-native';
import { Plus, Search, AlertTriangle, PackageCheck, PackageX, Package, CalendarClock } from 'lucide-react-native';

import { Button, Card, EmptyState, SkeletonRows, StatTile } from '@/components/ui';
import { ProductosTable } from '@/components/inventario/ProductosTable';
import { HistorialStockSheet } from '@/components/stock/HistorialStockSheet';
import { ProductoSheet } from '@/components/inventario/ProductoSheet';
import { VenderSheet } from '@/components/inventario/VenderSheet';
import { IngresarStockSheet } from '@/components/inventario/IngresarStockSheet';
import { AvisarReposicionSheet } from '@/components/inventario/AvisarReposicionSheet';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { productosVentaQuery, eliminarProductoVenta } from '@/services/firebase/inventario';
import { useAuth, selectIsAdmin, selectIsRecepcion } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { diasHasta, formatBs, normalizar } from '@/utils/format';
import type { ProductoVenta } from '@/types/domain';

type Filtro = 'todos' | 'bajo' | 'agotado' | 'vencen';

export function ProductosVentaScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const { data, loading } = useFirestoreCollection(productosVentaQuery);
  const isAdmin = useAuth(selectIsAdmin);
  const isRecepcion = useAuth(selectIsRecepcion);
  const perfil = useAuth((s) => s.perfil);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filtro>('todos');
  const [editing, setEditing] = useState<ProductoVenta | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [venderTarget, setVenderTarget] = useState<ProductoVenta | null>(null);
  const [ingresarTarget, setIngresarTarget] = useState<ProductoVenta | null>(null);
  const [avisarTarget, setAvisarTarget] = useState<ProductoVenta | null>(null);
  const [histTarget, setHistTarget] = useState<ProductoVenta | null>(null);

  const filtered = useMemo(() => {
    let arr = data;
    if (filter === 'bajo') arr = arr.filter((p) => p.stockBajo && p.stock > 0);
    else if (filter === 'agotado') arr = arr.filter((p) => p.stock === 0);
    else if (filter === 'vencen') {
      arr = arr.filter((p) => {
        const v = p.vencimiento?.toDate?.();
        const d = diasHasta(v ?? null);
        return d !== null && d <= 60;
      });
    }
    if (search.trim()) {
      const q = normalizar(search);
      arr = arr.filter(
        (p) =>
          normalizar(p.nombre).includes(q) ||
          normalizar(p.presentacion).includes(q) ||
          normalizar(p.categoria).includes(q),
      );
    }
    return arr;
  }, [data, search, filter]);

  const stats = useMemo(() => {
    const total = data.length;
    const bajos = data.filter((p) => p.stockBajo && p.stock > 0).length;
    const agotados = data.filter((p) => p.stock === 0).length;
    const venceran = data.filter((p) => {
      const v = p.vencimiento?.toDate?.();
      const d = diasHasta(v ?? null);
      return d !== null && d <= 60;
    }).length;
    const valor = data.reduce((acc, p) => acc + (p.precioCompra ?? 0) * p.stock, 0);
    return { total, bajos, agotados, venceran, valor };
  }, [data]);

  const rol = perfil?.rol ?? 'doctor';

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 18, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile
          label="Todos"
          value={String(stats.total)}
          accent="magenta"
          icon={<Package size={22} color={colors.magenta[700]} />}
          active={filter === 'todos'}
          onPress={() => setFilter('todos')}
        />
        <StatTile
          label="Stock bajo"
          value={String(stats.bajos)}
          accent="warning"
          icon={<AlertTriangle size={22} color={colors.warning[600]} />}
          hint="Por reponer"
          active={filter === 'bajo'}
          onPress={() => setFilter('bajo')}
        />
        <StatTile
          label="Agotados"
          value={String(stats.agotados)}
          accent="danger"
          icon={<PackageX size={22} color={colors.danger[600]} />}
          hint="Sin disponibilidad"
          active={filter === 'agotado'}
          onPress={() => setFilter('agotado')}
        />
        <StatTile
          label="Vencen pronto"
          value={String(stats.venceran)}
          accent="warning"
          hint="≤ 60 días"
          icon={<CalendarClock size={22} color={colors.warning[600]} />}
          active={filter === 'vencen'}
          onPress={() => setFilter('vencen')}
        />
        {isAdmin ? (
          <StatTile
            label="Valor inventario"
            value={formatBs(stats.valor)}
            accent="success"
            icon={<PackageCheck size={22} color={colors.success[600]} />}
            hint="A precio de compra"
          />
        ) : null}
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <View
            style={{
              flex: 1,
              minWidth: 220,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              backgroundColor: colors.crema[50],
              borderRadius: radius.full,
              paddingHorizontal: 14,
              height: 42,
              borderWidth: 1,
              borderColor: colors.ink[100],
            }}
          >
            <Search size={16} color={colors.ink[400]} />
            <TextInput
              placeholder="Buscar en farmacia…"
              placeholderTextColor={colors.ink[400]}
              value={search}
              onChangeText={setSearch}
              style={{
                flex: 1,
                fontSize: 14,
                color: colors.ink[900],
                fontFamily: typography.body.fontFamily,
                // @ts-ignore web only
                outlineStyle: 'none',
              }}
            />
          </View>

          {isAdmin ? (
            <Button
              label="Nuevo producto"
              variant="primary"
              size="md"
              iconLeft={<Plus size={16} color={colors.white} />}
              onPress={() => {
                setEditing(null);
                setEditSheetOpen(true);
              }}
            />
          ) : null}
        </View>
      </Card>

      {loading ? (
        <SkeletonRows rows={5} />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            illustration={search || filter !== 'todos' ? 'note' : 'shelf'}
            title={search || filter !== 'todos' ? 'Sin resultados' : 'Aún no hay productos'}
            message={
              search || filter !== 'todos'
                ? 'Probá con otro término o tocá «Todos» para quitar filtros.'
                : isAdmin
                  ? 'Cargá tu primer producto para empezar a llevar el control.'
                  : 'La administradora aún no cargó productos.'
            }
            actionLabel={isAdmin && !search && filter === 'todos' ? 'Cargar producto' : undefined}
            onAction={
              isAdmin && !search && filter === 'todos'
                ? () => {
                    setEditing(null);
                    setEditSheetOpen(true);
                  }
                : undefined
            }
          />
        </Card>
      ) : (
        <ProductosTable
          productos={filtered}
          tipo="venta"
          rol={rol}
          onEdit={
            isAdmin
              ? (p) => {
                  setEditing(p as ProductoVenta);
                  setEditSheetOpen(true);
                }
              : undefined
          }
          onVender={isAdmin || isRecepcion ? (p) => setVenderTarget(p as ProductoVenta) : undefined}
          onIngresarStock={isAdmin ? (p) => setIngresarTarget(p as ProductoVenta) : undefined}
          onAvisarReposicion={isRecepcion ? (p) => setAvisarTarget(p as ProductoVenta) : undefined}
          onEliminar={isAdmin ? (p) => eliminarProductoVenta(p.id) : undefined}
          onVerHistorial={(p) => setHistTarget(p as ProductoVenta)}
        />
      )}

      <HistorialStockSheet visible={!!histTarget} onClose={() => setHistTarget(null)} productoId={histTarget?.id ?? null} productoNombre={histTarget?.nombre ?? ''} />
      <ProductoSheet visible={editSheetOpen} onClose={() => setEditSheetOpen(false)} tipo="venta" producto={editing} />
      <VenderSheet visible={!!venderTarget} onClose={() => setVenderTarget(null)} producto={venderTarget} />
      <IngresarStockSheet visible={!!ingresarTarget} onClose={() => setIngresarTarget(null)} producto={ingresarTarget} tipo="venta" />
      <AvisarReposicionSheet visible={!!avisarTarget} onClose={() => setAvisarTarget(null)} producto={avisarTarget} origen="venta" />
    </ScrollView>
  );
}
